// General functions helper

import axios from "axios";
import moment from "moment";
import {FLASH_MESSAGES_DURATION} from "@/Config/constants";
import {routes} from "@/Config/routes";
import {logoutUser, refreshToken} from "./auth";
import {splitUppercase} from "./regex";
import {getStorageObject} from "./storage";
import {caseFormConfig} from "@/Config/forms";

/**
 * Change dates from timezone format to UTC format
 *
 * @param {object} data which needs parsing
 *
 * @returns {object} data parsed
 */
export const parseDatesUTC = (data) => {
    for (const key in data) {
        if (typeof (data[key]) == 'object' && moment.isDate(data[key])) {
            data[key] = moment(data[key]).format('YYYY-MM-DD HH:mm:ss');
        }
    }

    return data;
};

/**
 * Parse data which is coming from the API
 *
 * @param {object} data which needs parsing
 * @param {string} category from which the data is part of
 * @param {boolean} checkBooleans optional parameter for checking booleans or not
 *
 * @returns {object} data parsed
 */
export const parseDataFromAPI = (data, category, checkBooleans = true) => {
    let fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == category)?.fields,
        allFields = [],
        copyData = {...data};

    fieldsConfig.forEach((config) => {
        allFields = allFields.concat(config.fields);
    });

    for (const key in copyData) {
        const fieldConfig = allFields.find(field => field.name == key);

        if (fieldConfig) {
            // date ranges
            if (fieldConfig.type == 'date_range_start') {
                const getStartDate = moment(copyData[key]);

                if (getStartDate.isValid()) {
                    copyData[key] = [getStartDate.toDate()];
                }
            }

            if (fieldConfig.type == 'date_range_end') {
                const getEndDate = moment(copyData[key]);

                if (getEndDate.isValid() && copyData[fieldConfig.startKey]) {
                    copyData[fieldConfig.startKey].push(getEndDate.toDate());
                }
            }

            // dates
            if (fieldConfig.type == 'date') {
                const getDate = moment(copyData[key]);

                if (getDate.isValid()) {
                    copyData[key] = getDate.toDate();
                }
            }

            // booleans
            if (fieldConfig.type == 'boolean' && checkBooleans) {
                switch (copyData[key]) {
                    case true:
                        copyData[key] = 1;
                        break;

                    case false:
                        copyData[key] = 0;
                        break;

                    case null:
                        copyData[key] = '';
                        break;

                    default:
                        copyData[key] = '';
                        break;
                }
            }
        }
    }

    return copyData;
};

/**
 * Parse data which will be sent to the API
 *
 * @param {object} data which needs parsing
 * @param {string} category from which the data is part of
 *
 * @returns {object} data parsed
 */
export const parseDataToAPI = (data, category) => {
    let fieldsConfig = caseFormConfig.tabs.find(tab => tab.key == category)?.fields,
        allFields = [];

    fieldsConfig.forEach((config) => {
        allFields = allFields.concat(config.fields);
    });

    for (const key in data) {
        const fieldConfig = allFields.find(field => field.name == key);

        if (fieldConfig) {
            // dates
            if (fieldConfig.type == 'date' || fieldConfig.type == 'date_range_start' || fieldConfig.type == 'date_range_end') {
                const getDate = moment(data[key]);

                if (getDate.isValid()) {
                    data[key] = getDate.format('YYYY-MM-DD HH:mm:ss');
                }
            }

            // booleans
            if (fieldConfig.type == 'boolean') {
                if (data[key] != 0 && data[key] != 1) {
                    data[key] = '';
                }
            }
        }
    }

    return data;
};

/**
 *
 * @param time
 * @returns {Promise<unknown>}
 */
export const sleep = (time) => {
    return new Promise((resolve) => setTimeout(resolve, time));
};

let refreshing_token = false;
let pending_requests = [];

/**
 * Make customized api requests using axios
 *
 * @param {string} type the type of the request (get, post, delete etc)
 * @param {string} route route of the request (gets appended to the api base url)
 * @param {object} params optional object with data parameter, onSuccess and onFailure callback functions
 * @param {object} extraParams optional object with any extra parameters that will be appended to the axios request options
 */
export const apiRequest = (type, route, params, extraParams) => {
    const authToken = localStorage.getItem('auth_token');

    if (authToken || extraParams.noAuth) {
        const requestParams = {
            method: type,
            url: import.meta.env.VITE_API_URL + route,
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('auth_token')
            }
        };

        if (params.data) {
            params.data = parseDatesUTC(params.data);

            for (const key in params.data) {
                if (typeof (params.data[key]) == 'undefined') {
                    params.data[key] = null;
                }
            }

            if (type == 'get') {
                requestParams.params = params.data;

            } else {
                requestParams.data = params.data;
            }
        }

        if (extraParams) {
            if (extraParams.noAuth) {
                delete requestParams.headers;
            }

            if (extraParams.multipart) {
                requestParams.headers.ContentType = 'multipart/form-data';
            }
        }

        axios(requestParams)
            .then((response) => {
                if (params.onSuccess) {
                    const customResponse = {
                        data: null,
                        result: null,
                        pagination: null
                    };

                    if (response) {
                        if (response.data) {
                            customResponse.data = response.data;

                            if (response.data?.data?.result) {
                                customResponse.result = response.data.data.result;
                            }

                            if (response.data?.data?.pagination) {
                                customResponse.pagination = response.data.data.pagination;
                            }

                            if (response.data?.appVersion) {
                                localStorage.setItem('api_version', response.data?.appVersion);
                            }
                        }
                    }

                    params.onSuccess(customResponse);
                }
            })
            .catch(async (error) => {
                if (error?.response?.status && error.response.status === 401) {
                    if (!refreshing_token) {
                        refreshing_token = true;
                        if (await refreshToken()) {
                            await sleep(2000);
                            apiRequest(type, route, params, extraParams);
                        } else {
                            logoutUser();
                        }
                        refreshing_token = false;
                        pending_requests.map((request) => {
                            apiRequest(request.type, request.route, request.params, request.extraParams);
                        });
                        pending_requests = [];
                    } else {
                        if (!pending_requests.includes(route)) {
                            pending_requests.push({type, route, params, extraParams});
                        }
                    }
                } else {
                    if (params.onFailure) {
                        const customError = {
                            data: null,
                            response: null,
                            responseData: null,
                            status: null,
                            messages: null
                        };

                        if (error) {
                            customError.data = error;

                            if (error.response) {
                                customError.response = error.response;

                                if (error.response.data) {
                                    customError.responseData = error.response.data;

                                    if (error.response.data.errorMessage) {
                                        customError.messages = error.response.data.errorMessage;
                                    }
                                }

                                if (error.response.status) {
                                    customError.status = error.response.status;
                                }
                            }
                        }

                        params.onFailure(customError);
                    }
                }
            });

    } else {
        window.location.replace(import.meta.env.VITE_SITE_URL);
    }
};

/**
 * Downloads a blob content as a file.
 *
 * @param {Blob} content - The blob content to be downloaded.
 * @param {string} [filename] - The name of the file to be saved (optional).
 */
const downloadBlob = (content, filename) => {
    const url = window.URL.createObjectURL(content);
    const link = window.document.createElement('a');
    link.href = url;
    if (filename) {
        link.setAttribute('download', filename);
    }
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};


/**
 * Get a route's path
 *
 * @param {string} name the name of the route
 * @param {object} params route parameters
 * @param {string} type public or private
 *
 * @returns {string} the path of the route
 */
const findRoute = (name: string, params, type: 'public' | 'private') => {
    const findRoute = routes[type].find((item) => item.name === name);
    let path = findRoute?.path;
    if (!path) {
        throw new Error(`Route - ${name} not found`);
    }
    if (path && params) {
        for (const key in params) {
            path = path.replace(new RegExp(`:${key}\\??`), params[key]);
        }
    }
    return path;
};
/**
 * Get a private route path
 *
 * @param {string} name the name of the route
 * @param {object} params route parameters
 *
 * @returns {string} the path of the route
 */
export const url = (name: string, params = {}) => {
    return findRoute(name, params, 'private');
};

/**
 * Get a public route path
 *
 * @param {string} name the name of the route
 * @param {array} params route parameters
 *
 * @returns {string} the path of the route
 */
export const publicUrl = (name, params) => {
    return findRoute(name, params, 'public');
};

/**
 * Transform a camel case string into a phrase separated by space where the first letter is uppercase
 * Eg: from 'camelCase' to 'Camel Case'
 *
 * @param {string} string a camel case string
 *
 * @returns {string} the parsed string
 */
export const parseCamelCase = (string) => {
    let parsedString = string.split(splitUppercase).join(' ');

    parsedString = parsedString.charAt(0).toUpperCase() + parsedString.slice(1);

    return parsedString;
};

/**
 * Shows a notification message in the top right corner of the screen
 *
 * @param {object} ref html dom reference object
 * @param {string} type the type of message: success/info/warn/error
 * @param {string} message the message to be displayed
 */
export const toast = (ref, type, message) => {
    const title = type.charAt(0).toUpperCase() + type.slice(1);

    ref.current.show({
        severity: type,
        title: title,
        detail: message,
        life: FLASH_MESSAGES_DURATION
    });
};

/**
 * Show any flash messages saved in the local storage as toast notifications
 *
 * @param {object} ref html dom reference object
 */
export const showFlashMessages = (ref) => {
    const flashMessages = getStorageObject('flashMessages');

    if (flashMessages && flashMessages.length > 0) {
        for (let i = 0; i < flashMessages.length; i++) {
            toast(ref, flashMessages[i].type, flashMessages[i].message);
        }
    }

    localStorage.removeItem('flashMessages');
};

/**
 * Format a date coming from the API
 *
 * @param {string} date a date in string format
 *
 * @returns {string} the formatted date
 */
export const formatDate = (date, noTime = false) => {
    return moment(date).format('DD.MM.YYYY' + (!noTime ? ' HH:mm:ss' : ''));
};

export const fetchWithAuthentication = (url) => {
    const headers = new Headers();

    headers.set('Authorization', 'Bearer ' + localStorage.getItem('auth_token'));

    return fetch(url, {headers});
};

// export const getAuthImage = async (imageId, imageUrl) => {
export const getAuthImage = async (imageUrl) => {
    // Fetch the image.
    const response = await fetchWithAuthentication(imageUrl);

    // Create an object URL from the data.
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    return objectUrl;

    // Update the source of the image.
    // const imageElement = document.getElementById(imageId);
    // imageElement.src = objectUrl;
    // imageElement.onload = () => URL.revokeObjectURL(objectUrl);
};

export const downloadFile = async ({url, onSuccess, onFailure}) => {
    const options = {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('auth_token')
        }
    };
    try {
        const req = await fetch(import.meta.env.VITE_API_URL + url, options);
        if (req.ok) {
            const blob = await req.blob();
            const file = window.URL.createObjectURL(blob);
            window.location.assign(file);
            onSuccess();
        } else {
            onFailure(req.statusText, req.status);
        }
    } catch (e) {
        onFailure(e);
    }
};

export const downloadFileOutcome = async ({url, onSuccess, onFailure}) => {
    const options = {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('auth_token')
        }
    };
    try {
        const req = await fetch(import.meta.env.VITE_API_URL + url, options);
        if (req.ok) {
            const blob = await req.blob();
            downloadBlob(blob, 'outcome.txt');
            onSuccess();
        } else {
            onFailure(req.statusText, req.status);
        }
    } catch (e) {
        onFailure(e);
    }
};

/**
 * Format a file size
 *
 * @param {integer} bytes the file size in bytes
 *
 * @returns {string} the formatted size
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) {
        return '0 B';
    }

    if (bytes) {
        const k = 1000,
            dm = 3,
            sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
            i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }
};

/**
 * Convert a string from snake case to normal text with uppercase first letter words
 *
 * @param {string} inputString which needs parsing
 *
 * @returns {string} outputString data parsed
 */
export const snakeToNormalCase = (inputString) => {
    let outputString = inputString.toLowerCase(),
        outputArray = outputString.split('_');

    for (let i = 0; i < outputArray.length; i++) {
        outputArray[i] = outputArray[i].charAt(0).toUpperCase() + outputArray[i].slice(1);
    }

    outputString = outputArray.join(' ');

    return outputString;
};

export const toNormalCase = (inputString: string | null) => {
    if (!inputString) return null;
    return inputString
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Format big numbers using comma and decimal separator
 *
 * @param {float} value number to format
 *
 * @returns {string} formatted number
 */
export const formatBigNo = (value) => {
    return parseFloat(value).toLocaleString('en-GB', {maximumFractionDigits: 2, minimumFractionDigits: 2});
};

export const getBrowserType = () => {
    const browserMatch = regexp => {
        return regexp.test(navigator.userAgent);
    };

    if (browserMatch(/opr\//i) || !!window.opr) {
        return 'Opera';
    } else if (browserMatch(/edg/i)) {
        return 'Microsoft Edge';
    } else if (browserMatch(/chrome|chromium|crios/i)) {
        return 'Google Chrome';
    } else if (browserMatch(/firefox|fxios/i)) {
        return 'Mozilla Firefox';
    } else if (browserMatch(/safari/i)) {
        return 'Apple Safari';
    } else if (browserMatch(/trident/i)) {
        return 'Microsoft Internet Explorer';
    } else if (browserMatch(/ucbrowser/i)) {
        return 'UC Browser';
    } else if (browserMatch(/samsungbrowser/i)) {
        return 'Samsung Browser';
    } else {
        return 'Unknown browser';
    }
};

/**
 * Check if the current browser is Firefox
 *
 * @returns {boolean}
 */
export const isFirefox = () => {
    return getBrowserType() === 'Mozilla Firefox';
};

/**
 * Check if the current browser is Safari
 *
 * @returns {boolean}
 */
export const isSafari = () => {
    return getBrowserType() === 'Apple Safari';
};

/**
 * Check if the current browser is Edge
 *
 * @returns {boolean}
 */
export const isEdge = () => {
    return getBrowserType() === 'Microsoft Edge';
};

/**
 * Transform string into kebab string
 *
 * @param string
 * @returns {*}
 */
export const toKebab = (string) => {
    return string
        .split('')
        .map((letter, index) => {
            if (/[A-Z]/.test(letter)) {
                return ` ${letter.toLowerCase()}`;
            }
            return letter;
        })
        .join('')
        .trim()
        .replace(/[_\s]+/g, '-');
};

/**
 * Transform string into Title Case string
 *
 * @param string
 * @returns {*}
 */
export const toTitle = (string) => {
    return toKebab(string)
        .split('-')
        .map(word => {
            return word.slice(0, 1).toUpperCase() + word.slice(1);
        })
        .join(' ');
};
